import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';

const PassengerManagement = ({ tripId, passengers: initialPassengers = [], onUpdate, readOnly = false, optionalBaggageWeight }) => {
  const [passengers, setPassengers] = useState(initialPassengers || []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    salutation: '',
    name: '',
    weight: '',
    passengerType: 'Adult',
    baggageAllowanceKg: '',
    specialRequirements: ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPassengers(initialPassengers || []);
  }, [initialPassengers]);

  const calculateTotalWeight = () => {
    return passengers.reduce((sum, p) => sum + (parseFloat(p.weight) || 0), 0);
  };

  const handleOpenDialog = (index = null) => {
    if (index !== null) {
      const passenger = passengers[index];
      setFormData({
        salutation: passenger.salutation || '',
        name: passenger.name || '',
        weight: passenger.weight || '',
        passengerType: passenger.passengerType || 'Adult',
        baggageAllowanceKg: passenger.baggageAllowanceKg != null ? passenger.baggageAllowanceKg : '',
        specialRequirements: passenger.specialRequirements || ''
      });
      setEditingIndex(index);
    } else {
      setFormData({
        salutation: '',
        name: '',
        weight: '',
        passengerType: 'Adult',
        baggageAllowanceKg: '',
        specialRequirements: ''
      });
      setEditingIndex(null);
    }
    setError(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({
      salutation: '',
      name: '',
      weight: '',
      passengerType: 'Adult',
      baggageAllowanceKg: '',
      specialRequirements: ''
    });
    setEditingIndex(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.weight) {
      setError('Name and weight are required');
      return;
    }

    const weight = parseFloat(formData.weight);
    if (isNaN(weight) || weight <= 0) {
      setError('Weight must be a positive number');
      return;
    }

    const newPassenger = {
      salutation: formData.salutation || '',
      name: formData.name,
      weight: weight,
      passengerType: formData.passengerType || 'Adult',
      baggageAllowanceKg: formData.baggageAllowanceKg === '' || formData.baggageAllowanceKg == null
        ? undefined
        : Number(formData.baggageAllowanceKg),
      specialRequirements: formData.specialRequirements || ''
    };

    let updatedPassengers;
    if (editingIndex !== null) {
      updatedPassengers = [...passengers];
      updatedPassengers[editingIndex] = newPassenger;
    } else {
      updatedPassengers = [...passengers, newPassenger];
    }

    if (tripId && onUpdate) {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        await axios.put(`/api/sales/trips/${tripId}/passengers`, {
          passengers: updatedPassengers
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPassengers(updatedPassengers);
        if (onUpdate) {
          onUpdate(updatedPassengers, calculateTotalWeight());
        }
        handleCloseDialog();
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to save passengers');
      } finally {
        setLoading(false);
      }
    } else {
      // Local update only (for preview)
      setPassengers(updatedPassengers);
      if (onUpdate) {
        onUpdate(updatedPassengers, calculateTotalWeight());
      }
      handleCloseDialog();
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm('Are you sure you want to delete this passenger?')) {
      return;
    }

    const updatedPassengers = passengers.filter((_, i) => i !== index);

    if (tripId && onUpdate) {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        await axios.put(`/api/sales/trips/${tripId}/passengers`, {
          passengers: updatedPassengers
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPassengers(updatedPassengers);
        if (onUpdate) {
          onUpdate(updatedPassengers, calculateTotalWeight());
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete passenger');
      } finally {
        setLoading(false);
      }
    } else {
      setPassengers(updatedPassengers);
      if (onUpdate) {
        onUpdate(updatedPassengers, calculateTotalWeight());
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Passengers</Typography>
        {!readOnly && (
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            disabled={loading}
          >
            Add Passenger
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Weight (kg)</TableCell>
              <TableCell align="right">Baggage (kg)</TableCell>
              <TableCell>Special Requirements</TableCell>
              {!readOnly && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {passengers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={readOnly ? 5 : 6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No passengers added
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              passengers.map((passenger, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {passenger.salutation ? `${passenger.salutation} ` : ''}{passenger.name}
                  </TableCell>
                  <TableCell>{passenger.passengerType || 'Adult'}</TableCell>
                  <TableCell align="right">{passenger.weight} kg</TableCell>
                  <TableCell align="right">
                    {passenger.baggageAllowanceKg != null
                      ? `${passenger.baggageAllowanceKg} kg`
                      : (optionalBaggageWeight != null ? `${optionalBaggageWeight} kg` : '—')}
                  </TableCell>
                  <TableCell>{passenger.specialRequirements || '-'}</TableCell>
                  {!readOnly && (
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(index)}
                        disabled={loading}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(index)}
                        disabled={loading}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {passengers.length > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Total Weight: <strong>{(calculateTotalWeight() + (Number(optionalBaggageWeight) || 0)).toFixed(2)} kg</strong>
            {optionalBaggageWeight != null && Number(optionalBaggageWeight) !== 0 && (
              <Typography component="span" variant="caption" display="block" color="text.secondary">
                (passengers + baggage)
              </Typography>
            )}
          </Typography>
        </Box>
      )}
      {passengers.length === 0 && optionalBaggageWeight != null && Number(optionalBaggageWeight) > 0 && (
        <Box mt={2}>
          <Typography variant="body2" color="text.secondary">
            Total Weight: <strong>{(Number(optionalBaggageWeight) || 0).toFixed(2)} kg</strong>
            <Typography component="span" variant="caption" display="block" color="text.secondary">
              (baggage only)
            </Typography>
          </Typography>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingIndex !== null ? 'Edit Passenger' : 'Add Passenger'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Salutation</InputLabel>
              <Select
                value={formData.salutation}
                onChange={(e) => setFormData({ ...formData, salutation: e.target.value })}
                label="Salutation"
              >
                <MenuItem value="">None</MenuItem>
                <MenuItem value="Capt">Capt</MenuItem>
                <MenuItem value="Dr">Dr</MenuItem>
                <MenuItem value="Mr">Mr</MenuItem>
                <MenuItem value="Mrs">Mrs</MenuItem>
                <MenuItem value="Ms">Ms</MenuItem>
                <MenuItem value="Adv">Adv</MenuItem>
                <MenuItem value="Prof">Prof</MenuItem>
                <MenuItem value="Col">Col</MenuItem>
                <MenuItem value="Maj">Maj</MenuItem>
                <MenuItem value="Lt">Lt</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Passenger Type</InputLabel>
              <Select
                value={formData.passengerType || 'Adult'}
                onChange={(e) => setFormData({ ...formData, passengerType: e.target.value })}
                label="Passenger Type"
              >
                <MenuItem value="Adult">Adult</MenuItem>
                <MenuItem value="Child">Child</MenuItem>
                <MenuItem value="Infant">Infant</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Weight (kg)"
              type="number"
              value={formData.weight}
              onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
              margin="normal"
              required
              inputProps={{ min: 0, step: 0.1 }}
            />
            <TextField
              fullWidth
              label="Baggage Allowance (kg)"
              type="number"
              value={formData.baggageAllowanceKg}
              onChange={(e) => setFormData({ ...formData, baggageAllowanceKg: e.target.value })}
              margin="normal"
              inputProps={{ min: 0, step: 0.1 }}
              helperText={
                optionalBaggageWeight != null
                  ? `Leave empty to use trip default (${optionalBaggageWeight} kg)`
                  : 'Optional; leave empty to use trip baggage default'
              }
            />
            <TextField
              fullWidth
              label="Special Requirements"
              value={formData.specialRequirements}
              onChange={(e) => setFormData({ ...formData, specialRequirements: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PassengerManagement;

